/**
 * 404 page (`*` route catch-all).
 *
 * Renders inside `AppLayout` so the user keeps the sidebar / navigation
 * — losing the chrome on a 404 strands them with no way back.
 */
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Compass } from "lucide-react";

import { AppLayout } from "@/components/AppLayout";
import { Card, PageHero } from "@/components/Card";
import { Button } from "@/components/ui/button";

const NotFound = () => {
  const location = useLocation();

  // Logging gives ops a clean signal when a deep link goes stale (rename
  // / remove). Replace with a real tracker once §16 lands.
  useEffect(() => {
    if (typeof window !== "undefined") {
      console.warn("[404]", location.pathname + location.search);
    }
  }, [location.pathname, location.search]);

  const suggestions: Array<{ to: string; label: string; blurb: string }> = [
    { to: "/",          label: "Overview",  blurb: "Today's KPIs and the actions Claude flagged." },
    { to: "/orders",    label: "Orders",    blurb: "Triage the queue, hold high-RTO COD orders." },
    { to: "/alerts",    label: "Alerts",    blurb: "RTO spikes, SLA breaches, volume anomalies." },
    { to: "/claude",    label: "Claude",    blurb: "Ask the agent — it can read every surface." },
  ];

  return (
    <AppLayout>
      <div className="rf-stagger space-y-6">
        <PageHero
          eyebrow="404 · Page not found"
          title={<>That page isn't here</>}
          subtitle={
            <>
              We couldn't find <code className="font-mono text-[12.5px]">{location.pathname}</code>. It may
              have moved, or the link may be stale. Pick a surface below to keep moving.
            </>
          }
        />

        <Card className="px-6 py-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {suggestions.map((s) => (
              <Link
                key={s.to}
                to={s.to}
                className="group flex items-start gap-3 rounded-md border border-line bg-canvas-2/40 px-4 py-3 transition hover:border-accent hover:bg-canvas-2"
              >
                <Compass className="mt-0.5 h-4 w-4 shrink-0 text-ink-3 group-hover:text-accent" aria-hidden="true" />
                <div>
                  <div className="font-medium text-ink">{s.label}</div>
                  <div className="text-[12.5px] text-ink-3">{s.blurb}</div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-6 flex gap-2">
            <Button variant="outline" onClick={() => window.history.back()} className="gap-1.5">
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
              Go back
            </Button>
            <Button asChild>
              <Link to="/">Take me home</Link>
            </Button>
          </div>
        </Card>
      </div>
    </AppLayout>
  );
};

export default NotFound;
